import { Button, Stack, Typography } from '@mui/material';
import type { FormEvent, ReactNode } from 'react';
import StyledForm from '../../../forms/shared/StyledForm';
import { neobetRaisedActionButtonStyles } from '../../../../styles/components/neobetRaisedActionButton';
import { mergeSx } from '../../../../utils/styles';
import NeobetRaisedActionButtonLabel from '../molecules/NeobetRaisedActionButtonLabel';

type NeobetAuthFormShellProps = {
  title: string;
  submitTitle: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose?: () => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  children: ReactNode;
  footerText?: string;
  footerActionLabel?: string;
  onFooterAction?: () => void;
  footerTestId?: string;
  footerActionTestId?: string;
  testId: string;
};

const styles = {
  form: {
    width: '100%',
    maxWidth: { md: 520 },
    borderRadius: 2,
    overflow: 'hidden',
    '@media (max-width:899.95px)': {
      minWidth: '100%',
      borderRadius: 0,
      borderLeft: 'none',
      borderRight: 'none',
      borderBottom: 'none',
    },
    '& .MuiDialogTitle-root': {
      p: { xs: '24px 20px 20px', md: '28px 24px 24px' },
      color: 'neutral.50',
      typography: 'h1',
      fontWeight: 700,
      lineHeight: { xs: '34px', md: '40px' },
      letterSpacing: 0,
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      textAlign: 'left',
    },
    '& .MuiDialogTitle-root .MuiIconButton-root': {
      color: 'neutral.500',
    },
    '& .MuiDialogContent-root': {
      p: { xs: '0 20px 8px', md: '0 24px 8px' },
    },
    '& .MuiDialogActions-root': {
      p: { xs: '8px 20px 8px', md: '12px 24px 8px' },
    },
  },
  footer: {
    mt: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    px: 3,
    pb: 1,
  },
  footerText: {
    color: 'text.secondary',
    typography: 'body2',
  },
  linkButton: {
    minWidth: 'auto',
    p: 0,
    color: 'primary.main',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '20px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: 'primary.300',
    },
  },
};

const NeobetAuthFormShell = ({
  title,
  submitTitle,
  onSubmit,
  onClose,
  onBack,
  isSubmitting,
  submitDisabled,
  children,
  footerText,
  footerActionLabel,
  onFooterAction,
  footerTestId,
  footerActionTestId,
  testId,
}: NeobetAuthFormShellProps) => {
  return (
    <>
      <StyledForm
        title={title}
        submitTitle={submitTitle}
        onSubmit={onSubmit}
        onClose={onClose}
        onBack={onBack}
        isSubmitting={isSubmitting}
        sx={styles.form}
        submitButton={{
          disabled: submitDisabled,
          testId: `${testId}-submit`,
          label: <NeobetRaisedActionButtonLabel>{submitTitle}</NeobetRaisedActionButtonLabel>,
          sx: mergeSx(neobetRaisedActionButtonStyles.button, neobetRaisedActionButtonStyles.variants.authForm),
        }}
      >
        {children}
      </StyledForm>
      {footerText && footerActionLabel && onFooterAction ? (
        <Stack direction='row' sx={styles.footer} data-testid={footerTestId}>
          <Typography sx={styles.footerText}>{footerText}</Typography>
          <Button
            type='button'
            variant='text'
            onClick={onFooterAction}
            data-testid={footerActionTestId}
            sx={styles.linkButton}
          >
            {footerActionLabel}
          </Button>
        </Stack>
      ) : null}
    </>
  );
};

export default NeobetAuthFormShell;
